import React from 'react';
import { Flex, Card, CardContent, Heading, Text, Button, ThemeProvider, Divider, PasswordField, Spinner, PasswordStrengthIndicator, usePasswordStrength } from '@chatsdet/ui';
import { Link, useSearch, useNavigate } from '@tanstack/react-router';
import { useResetPassword, EmailAuthError } from '../../hooks/useEmailAuth';
import { BRAND } from '../../brand';
import { CofactorMark } from '../components/CofactorMark';

interface ResetPasswordSearch {
  token?: string;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ResetPasswordSearch;
  const { token } = search;
  const name = BRAND.name;

  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const resetPasswordMutation = useResetPassword();
  const { valid: passwordValid, reasons: passwordReasons } = usePasswordStrength(password);

  const passwordsMatch = password === confirmPassword && password.length > 0;

  // Explain why the submit button is disabled, so it never greys out silently.
  const submitDisabledReason = (() => {
    if (!password) return 'Enter a new password to continue';
    if (!passwordValid) return passwordReasons.join('. ');
    if (!confirmPassword) return 'Confirm your new password to continue';
    if (!passwordsMatch) return 'Passwords do not match';
    return null;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength — surface the specific unmet requirement(s).
    if (!passwordValid) {
      setError(passwordReasons.join('. '));
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({ token, password });
      setSuccess(true);
    } catch (err) {
      if (err instanceof EmailAuthError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  // Show error if no token
  if (!token && !success) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

          <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
            <CardContent className="p-8 space-y-8">
              <div className="text-center space-y-3">
                <div className="flex justify-center mb-2">
                  <CofactorMark size={80} className="text-foreground" />
                </div>
                <Heading level={1} className="text-2xl font-bold text-foreground">
                  Invalid Reset Link
                </Heading>
              </div>

              <Divider />

              <div className="space-y-4 text-center">
                <Text className="text-muted-foreground">
                  This password reset link is invalid or has expired.
                </Text>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onPress={() => navigate({ to: '/forgot-password' })}
                >
                  Request a new reset link
                </Button>
                <Link
                  to="/login"
                  className="block text-sm text-primary hover:underline"
                >
                  Back to sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

        <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
          <CardContent className="p-8 space-y-8">
            {/* Logo/Brand Section */}
            <div className="text-center space-y-3">
              <div className="flex justify-center mb-2">
                <CofactorMark size={80} className="text-foreground" />
              </div>
              <Heading level={1} className="text-2xl font-bold text-foreground">
                {success ? 'Password Reset!' : 'Set a new password'}
              </Heading>
              {!success && (
                <Text className="text-muted-foreground">
                  Enter your new password below
                </Text>
              )}
            </div>

            <Divider />

            {success ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-success"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <Text className="text-muted-foreground">
                    Your password has been successfully reset.
                  </Text>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onPress={() => navigate({ to: '/login' })}
                >
                  Sign in with new password
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Text size="sm" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                <div className="space-y-2">
                  <PasswordField
                    label="New Password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    isRequired
                  />
                  <PasswordStrengthIndicator password={password} />
                </div>

                <PasswordField
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  autoComplete="new-password"
                  isRequired
                  errorMessage={
                    confirmPassword && !passwordsMatch
                      ? 'Passwords do not match'
                      : undefined
                  }
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isDisabled={
                    resetPasswordMutation.isPending || !passwordValid || !passwordsMatch
                  }
                >
                  {resetPasswordMutation.isPending ? (
                    <Flex align="center" gap="sm">
                      <Spinner size="sm" />
                      <span>Resetting password...</span>
                    </Flex>
                  ) : (
                    'Reset password'
                  )}
                </Button>

                {submitDisabledReason && !resetPasswordMutation.isPending && (
                  <Text size="sm" className="text-center text-muted-foreground">
                    {submitDisabledReason}
                  </Text>
                )}

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-primary hover:underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <Text size="xs" className="text-muted-foreground">
            © {new Date().getFullYear()} {name}. All rights reserved.
          </Text>
        </div>
      </div>
    </ThemeProvider>
  );
}
